import React from "react";
import { History, CheckCircle, XCircle, RotateCcw } from "lucide-react";

const ACTION_META = {
  resolved:    { label: "Resolved",    color: "bg-emerald-50 text-emerald-700 ring-emerald-200",  Icon: CheckCircle },
  closed:      { label: "Closed",      color: "bg-emerald-50 text-emerald-700 ring-emerald-200",  Icon: CheckCircle },
  completed:   { label: "Completed",   color: "bg-emerald-50 text-emerald-700 ring-emerald-200",  Icon: CheckCircle },
  implemented: { label: "Implemented", color: "bg-cyan-50 text-cyan-700 ring-cyan-200",           Icon: CheckCircle },
  canceled:    { label: "Cancelled",   color: "bg-slate-100 text-slate-700 ring-slate-200",      Icon: XCircle },
  rejected:    { label: "Rejected",    color: "bg-red-50 text-red-700 ring-red-200",             Icon: XCircle },
  reopened:    { label: "Reopened",    color: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200", Icon: RotateCcw },
};

function fmt(dt) {
  return new Date(dt).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

/**
 * ClosureHistory — read-only audit list of all
 * resolution/closure/cancellation/reopen events on a ticket.
 * Entries are always rendered chronologically (oldest first), immutable.
 */
export default function ClosureHistory({ entries = [], title = "Resolution history" }) {
  const sorted = (entries || []).slice().sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  );

  return (
    <div className="card p-5">
      <h2 className="font-semibold mb-3 flex items-center gap-2 text-gray-800">
        <History size={16} className="text-brand-500" />
        {title}
        {sorted.length > 0 && <span className="text-xs font-normal text-gray-500">({sorted.length})</span>}
      </h2>

      {sorted.length === 0 ? (
        <p className="text-sm text-gray-500 italic">
          No resolution / closure entries yet. Notes will be captured each time the ticket is resolved, closed, cancelled, or reopened.
        </p>
      ) : (
        <ol className="relative border-l-2 border-slate-100 ml-3 space-y-4">
          {sorted.map((e, i) => {
            const meta = ACTION_META[e.action] || { label: e.action, color: "bg-slate-100 text-slate-700 ring-slate-200", Icon: CheckCircle };
            const { Icon } = meta;
            return (
              <li key={e.id || i} className="ml-4">
                <div className={`absolute -left-2.5 w-5 h-5 rounded-full ring-4 ring-white flex items-center justify-center ${meta.color.split(" ")[0]} ${meta.color.split(" ")[1]}`}>
                  <Icon size={11} />
                </div>
                <div className="flex items-center flex-wrap gap-2 text-xs">
                  <span className={`badge ${meta.color}`}>{meta.label}</span>
                  <span className="text-slate-500">{fmt(e.created_at)}</span>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-700 font-semibold">{e.author_name}</span>
                  <span className="text-slate-400">({e.author_role})</span>
                </div>
                {e.notes ? (
                  <div className="mt-1.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 whitespace-pre-wrap break-words">
                    {e.notes}
                  </div>
                ) : (
                  <div className="mt-1.5 text-xs text-slate-400 italic">No notes captured at this transition.</div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
